import React, {useCallback, useMemo, useRef} from "react";
import Utils, {getFirstScrollingAncestor} from "../../../../../core/services/utils/utils";
import Form, {MuiRFF, Yup} from '../../../form';
import {Box, useMediaQuery} from "@mui/material";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import {useDynamicRowVirtualizer} from "../../../../hooks/use-row-virtualizer";
import {toColumnOption} from "../../../text-fields/autocomplete/import-csv-column-autocomplete";
import ColumnMappingRow from "./column-mapping-row";

const SORT_MAPPED_FIELDS_FIRST_THRESHOLD = 50;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function hasColumnMappingValue(value) {
    if (value == null) {
        return false;
    }
    const title = typeof value === 'object' ? value.title : value;
    return !!title?.toString()?.trim();
}

/**
 * When field count exceeds the threshold, mapped fields stay at the top and
 * unmapped fields sink to the bottom. Original order is preserved within each group.
 *
 * @param {Array<{ name: string, title: string }>} fields
 * @param {Record<string, unknown>} valuesByFieldName
 * @returns {Array<{ name: string, title: string }>}
 */
function sortFieldsByMappedValues(fields, valuesByFieldName) {
    if (fields.length <= SORT_MAPPED_FIELDS_FIRST_THRESHOLD) {
        return fields;
    }

    const withMapping = [];
    const withoutMapping = [];

    for (const field of fields) {
        if (hasColumnMappingValue(valuesByFieldName?.[field.name])) {
            withMapping.push(field);
        } else {
            withoutMapping.push(field);
        }
    }

    return [...withMapping, ...withoutMapping];
}


/**
 * Builds an initial field-to-column mapping by assigning the strongest matches first,
 * then filling any remaining fields top-to-bottom from leftover columns.
 *
 * @param {Array<{ name: string, title: string, optional?: boolean }>} fields
 * @param {string[]} columns
 * @returns {Record<string, string | null>}
 */
function buildAutoColumnMapping(fields, columns) {
    if (!fields?.length || !columns?.length) {
        return Object.fromEntries((fields ?? []).map((field) => [field.name, null]));
    }

    const fieldIndexByName = new Map(fields.map((field, index) => [field.name, index]));

    /** @type {{ fieldName: string, column: string, score: number }[]} */
    const rankedPairs = [];
    for (const field of fields) {
        for (const column of columns) {
            rankedPairs.push({
                fieldName: field.name,
                column,
                score: Utils.getStringSimilarityPercentage(column, field.title),
            });
        }
    }

    rankedPairs.sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        return (fieldIndexByName.get(a.fieldName) ?? 0) - (fieldIndexByName.get(b.fieldName) ?? 0);
    });

    const assignedFields = new Set();
    const usedColumns = new Set();
    /** @type {Record<string, string | null>} */
    const mapping = Object.fromEntries(fields.map((field) => [field.name, null]));

    for (const pair of rankedPairs) {
        if (assignedFields.has(pair.fieldName) || usedColumns.has(pair.column)) {
            continue;
        }
        if (pair.score <= 0) {
            continue;
        }
        assignedFields.add(pair.fieldName);
        usedColumns.add(pair.column);
        mapping[pair.fieldName] = pair.column;
    }

    for (const field of fields) {
        if (assignedFields.has(field.name)) {
            continue;
        }
        const availableColumns = columns.filter((column) => !usedColumns.has(column));
        const bestMatch = Utils.findBestMatchingString(availableColumns, field.title);
        if (bestMatch) {
            usedColumns.add(bestMatch);
            mapping[field.name] = bestMatch;
        }
        assignedFields.add(field.name);
    }

    return mapping;
}


/**
 * Creates a Yup schema for a column mapping field.
 * @param optional
 * @returns {*}
 */
const createColumnMappingFieldSchema = (optional) => {
    if (optional) {
        return Yup.mixed().nullable();
    }
    return Yup.mixed()
        .nullable()
        .required(ValidateMessages.required)
        .test('column-selected', ValidateMessages.required, (value) => {
            if (value == null) {
                return false;
            }
            const title = typeof value === 'object' ? value.title : value;
            return !!title?.toString()?.trim();
        });
};

const MAPPING_ROW_MIN_HEIGHT_SMALL_SCREENS = 89.14;
const MAPPING_ROW_MIN_HEIGHT = 53.13;

/**
 * The column mapper component for the ImportCsvFile component.
 * @param {string[][]}rows
 * @param {string[]} columns
 * @param {string?} formId
 * @param {string?} formClassName
 * @param {number?} offsetStartingHeight
 * @param {number?} offsetEndingHeight
 * @param {Record<string, any>?} initialValues
 * @param {function} onParse
 * @param {Array<ImportCsvFileRequiredField>} fields
 * @param {function(): HTMLElement | null} [getScrollParent]
 * @returns {JSX.Element}
 */
const ImportCsvFileColumnMapper = ({
                                       rows,
                                       columns,
                                       onParse,
                                       fields,
                                       formId,
                                       formClassName,
                                       initialValues: initialValuesProps,
                                       getScrollParent: getScrollParentProp,
                                       offsetStartingHeight,
                                       offsetEndingHeight,
                                   }) => {
    const rootRef = useRef(null);
    const isInSmallerScreens = useMediaQuery((theme) => theme.breakpoints.down('md'));

    /**
     * Gets the scroll parent for the virtualizer. If a getScrollParent function is provided via props, it will use that. Otherwise, it will find the nearest scroll parent of the root element.
     * @type {(function(): (*))|*}
     */
    const getScrollParent = useCallback(() => {
        if (getScrollParentProp) {
            return getScrollParentProp();
        }
        if (!rootRef.current) {
            return document.body;
        }
        return getFirstScrollingAncestor(rootRef.current);
    }, [getScrollParentProp]);

    const schema = useMemo(() =>
        Yup.object().shape(Object.fromEntries(fields.map(e => [
            e.name,
            createColumnMappingFieldSchema(e.optional),
        ]))), [fields]);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => {
        if (initialValuesProps && Object.keys(initialValuesProps).length > 0) {
            return Object.fromEntries(
                fields.map((field) => [
                    field.name,
                    toColumnOption(initialValuesProps[field.name] ?? initialValuesProps[field.title]),
                ]),
            );
        }
        const autoMapping = buildAutoColumnMapping(fields, columns);
        return Object.fromEntries(
            Object.entries(autoMapping).map(([fieldName, columnTitle]) => [
                fieldName,
                toColumnOption(columnTitle),
            ]),
        );
    }, [columns, fields, initialValuesProps]);

    const sortedFields = useMemo(
        () => sortFieldsByMappedValues(fields, initialValues),
        [fields, initialValues],
    );

    const rowVirtualizer = useDynamicRowVirtualizer({
        data: sortedFields,
        estimateRowHeight: isInSmallerScreens ? MAPPING_ROW_MIN_HEIGHT_SMALL_SCREENS : MAPPING_ROW_MIN_HEIGHT,
        minRowHeight: MAPPING_ROW_MIN_HEIGHT,
        overscan: 4,
        getScrollParent,
        getItemKey: (field, index) => field.name ?? index,
        offsetStartingHeight,
        offsetEndingHeight,
    });

    /**
     * The submit handler for the form.
     * @type {(function(*): void)}
     */
    const onSubmit = useCallback((values) => {
        const columnsMapping = Object.fromEntries(
            fields
                .map((field) => {
                    const value = values[field.name];
                    const columnTitle = typeof value === 'object' && value != null ? value.title : value;
                    return [field.name, columnTitle];
                })
                .filter(([, columnTitle]) => !!columnTitle?.toString()?.trim()),
        );

        const parsedRows = rows.map(row => {
            const rowObject = {};
            const rowData =
                Array.isArray(row)
                    ? Object.fromEntries(columns.map((col, idx) => [col, row[idx]]))
                    : Object.fromEntries(
                        Object.entries(row).map(([key, value]) => {
                            const field = fields.find(f => f.name === key);
                            return field ? [field.title, value] : null;
                        }).filter(e => !!e)
                    );

            for (const field of fields) {
                const mappedValue = values[field.name];
                const columnTitle = typeof mappedValue === 'object' && mappedValue != null
                    ? mappedValue.title
                    : mappedValue;
                if (!columnTitle?.toString()?.trim()) {
                    continue;
                }

                const value = rowData[columnTitle];
                rowObject[field.name] = field.format ? field.format(value) : value;
            }

            return rowObject;
        });

        if (onParse) {
            onParse(parsedRows, columnsMapping);
        }
    }, [columns, onParse, fields, rows]);

    return (
        <Form
            validate={validate}
            initialValues={initialValues}
            onSubmit={onSubmit}
            formProps={{id: formId, className: formClassName,}}
        >
            {() => (
                <Box ref={rootRef} className={'w-100'}>
                    <Box sx={{height: rowVirtualizer.topSpacerHeight}}/>
                    {
                        rowVirtualizer.items.map((field, visibleIndex) => (
                            <Box
                                key={rowVirtualizer.keyFor(visibleIndex)}
                                ref={rowVirtualizer.rowRef(visibleIndex)}
                            >
                                <ColumnMappingRow
                                    field={field}
                                    columns={columns}
                                    required={required[field.name]}
                                />
                            </Box>
                        ))
                    }
                    <Box sx={{height: rowVirtualizer.bottomSpacerHeight}}/>
                </Box>
            )}
        </Form>
    );
};

export default ImportCsvFileColumnMapper;
